/**
 * A collapsible container that shows/hides content when the header is clicked
 * Includes an arrow indicator that rotates based on open state
 */
import styleText from './collapse-content.css?raw';
import { sendEv } from '../../data/events.ts';

export class CollapseContent extends HTMLElement {
  static readonly tag = 'collapse-content' as const;

  // Shadow DOM elements
  private _defaultHeaderText: HTMLDivElement | undefined;
  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  
  // Observed attributes
  static get observedAttributes(): string[] {
    return ['open', 'header-text'];
  }
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      CollapseContent._sheet
    ];
    this._render();
    this._setupEventListeners();
  }
  
  /**
   * Renders the component structure and styles using HTML templates
   */
  private _render(): void {
    if (!this.shadowRoot) return;
    
    // Create template
    const template = document.createElement('template');
    
    // Set default header text
    const headerText = this.getAttribute('header-text') ?? 'Collapsible Section';
    
    // Define the component HTML structure with styles
    template.innerHTML = `
      <div class="container">
        <div class="header">
          <div class="default-header-text">${headerText}</div>
          <slot name="header"></slot>
          <span class="arrow"></span>
        </div>
        <div class="content">
          <slot></slot>
        </div>
      </div>
    `;
    
    // Clone and append the template content
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    // Store reference to default header text for later updates
    this._defaultHeaderText = this.shadowRoot.querySelector('.default-header-text');
    
    // Check if header slot has content
    this._checkHeaderSlot();
  }
  
  /**
   * Sets up event listeners for the component
   */
  private _setupEventListeners(): void {
    if (!this.shadowRoot) return;
    
    // Add click listener to header
    const header = this.shadowRoot.querySelector('.header');
    header?.addEventListener('click', () => {
      this.toggleOpen();
    });
    
    // Listen for slot changes to show/hide default header
    const headerSlot = this.shadowRoot.querySelector('slot[name="header"]');
    headerSlot?.addEventListener('slotchange', () => {
      this._checkHeaderSlot();
    });
  }
  
  /**
   * Checks if the header slot has content and shows/hides default header accordingly
   */
  private _checkHeaderSlot(): void {
    if (!this.shadowRoot) return;
    
    const headerSlot = this.shadowRoot.querySelector('slot[name="header"]') as HTMLSlotElement;
    if (headerSlot) {
      const hasContent = headerSlot.assignedNodes().length > 0;
      if (this._defaultHeaderText) {
        this._defaultHeaderText.style.display = hasContent ? 'none' : 'block';
      }
    }
  }
  
  /**
   * Toggles the open state of the collapsible content
   */
  public toggleOpen(): void {
    const isOpen = this.hasAttribute('open');
    if (isOpen) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
  }
  
  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'open' && oldValue !== newValue) {
      // Dispatch event when open state changes
      sendEv(this, 'toggle', {
        composed: false,
        detail: { open: this.hasAttribute('open') },
      });
    } else if (name === 'header-text' && this._defaultHeaderText) {
      // Update default header text when attribute changes
      this._defaultHeaderText.textContent = newValue ?? 'Collapsible Section';
    }
  }
  
  /**
   * Sets the open state programmatically
   */
  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }
  
  /**
   * Gets the current open state
   */
  get open(): boolean {
    return this.hasAttribute('open');
  }
  
  /**
   * Sets the header text programmatically
   */
  set headerText(value: string) {
    this.setAttribute('header-text', value);
  }
  
  /**
   * Gets the current header text
   */
  get headerText(): string {
    return this.getAttribute('header-text') ?? 'Show / Hide';
  }
}

// Check if the element is already defined before registering it
if (!customElements.get(CollapseContent.tag)) {
  customElements.define(CollapseContent.tag, CollapseContent);
} else {
  console.warn("collapse-content already defined");
}
